/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { Else, If, Then, Unless } from "react-if";
import { Box } from "@nordcloud/gnui";
import { PlanSortFields, SortOrder, Plan } from "~/generated/graphql";
import {
  LoaderWrap,
  NoData,
  Pagination,
  SortSelector,
  TableWrapper,
  TableToolbar,
  TableToolbarSearch,
} from "~/components";
import { AppliedFiltersSection } from "~/components/AppliedFiltersSection";
import {
  minSearchLength,
  TableSearchInput,
  getNoSearchResultsMessage,
} from "~/components/SearchInput";
import { useQueryState } from "~/hooks";
import { useAppliedFilters } from "~/hooks/useAppliedFilters";
import { isEmpty, isNotNil, isNotEmpty } from "~/tools";
import { useGetPlans } from "~/views/plans/hooks";
import { sortOptions, QueryGetPlans } from "../components/constants";
import { preparePlansFilterBy } from "../components/planFilterBuilders";
import { PlanFiltersSidebar } from "../components/PlanFiltersSidebar";
import { usePlansAppliedFilters } from "../hooks/usePlansAppliedFilters";
import { columns } from "./PlansTableColumns";

type Props = {
  customActionId?: string;
};

export function PlansTable({ customActionId }: Props) {
  const { queryState, updateQueryState } = useQueryState<QueryGetPlans>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const plansAppliedFilterDefinitions = usePlansAppliedFilters();
  const appliedFiltersState = useAppliedFilters({
    queryState,
    updateQueryState,
    definitions: plansAppliedFilterDefinitions,
  });

  useEffect(() => {
    if (customActionId && !queryState.planActionIds?.includes(customActionId)) {
      updateQueryState(
        {
          ...queryState,
          planActionIds: [customActionId],
        },
        "replace"
      );
    }
  }, [customActionId, queryState, updateQueryState]);

  const handleSortDirection = (order: SortOrder) => {
    updateQueryState({
      ...queryState,
      order,
    });
  };

  const handleSortField = (field: PlanSortFields) => {
    updateQueryState({
      ...queryState,
      field,
    });
  };

  const handlePhraseField = (field: string | undefined) => {
    updateQueryState({
      ...queryState,
      searchPhrase: field,
    });
  };

  const { data, loading, count } = useGetPlans({
    page: queryState.page,
    limit: queryState.limit,
    filter: {
      filterBy: preparePlansFilterBy(queryState),
      phrase: isNotEmpty(queryState.searchPhrase?.trim() || "")
        ? queryState.searchPhrase
        : undefined,
    },
    sort: {
      field: queryState.field ?? PlanSortFields.Name,
      order: queryState.order ?? SortOrder.Asc,
    },
  });

  const isSearchAllowed =
    !queryState.searchPhrase ||
    queryState.searchPhrase.length >= minSearchLength;

  return (
    <>
      <Box boxStyle="lightGrey" spacing="spacing04">
        <TableToolbar>
          <SortSelector<PlanSortFields>
            options={sortOptions}
            value={queryState.field ?? PlanSortFields.Name}
            direction={queryState.order ?? SortOrder.Asc}
            onSelect={handleSortField}
            onSortChange={handleSortDirection}
          />
          <TableToolbarSearch>
            <TableSearchInput<QueryGetPlans>
              onSubmit={handlePhraseField}
              placeholder={`Type a Keyword (e.g. Plan Name, Plan ID, etc.)`}
            />
          </TableToolbarSearch>
          <Unless condition={customActionId}>
            <PlanFiltersSidebar resultsCount={count ?? 0} />
          </Unless>
        </TableToolbar>
        <AppliedFiltersSection state={appliedFiltersState} />
      </Box>
      <If condition={!isSearchAllowed}>
        <Then>
          <NoData hasIcon message={getNoSearchResultsMessage()} />
        </Then>
        <Else>
          <LoaderWrap loading={loading} inContent>
            <If condition={isEmpty(data)}>
              <Then>
                <NoData hasIcon message={"There are no Plans."} />
              </Then>
              <Else>
                <TableWrapper<Partial<Plan>>
                  loading={loading}
                  data={data.filter(isNotNil) as Plan[]}
                  columns={columns()}
                  rowSelection={rowSelection}
                  setRowSelection={setRowSelection}
                  stripped
                />
                <Pagination count={count} />
              </Else>
            </If>
          </LoaderWrap>
        </Else>
      </If>
    </>
  );
}
